import React, { useState } from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import { ArrowRight } from "../icon/arrow-left";
import {
  cn,
  toPlainText,
  truncateText,
  formatDurationMinutes,
  sumDurationsToSeconds,
  formatTotalDuration,
  formatTotalHours,
} from "../../lib/helpers";
import {
  faGraduationCap,
  faCompass,
  faShieldHalved,
  faClock,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const query = graphql`
  query TrainingPlaylistQuery {
    trainings: allSanityTraining(
      sort: { fields: [orderRank], order: ASC }
      filter: { status: { eq: "published" }, slug: { current: { ne: null } } }
    ) {
      edges {
        node {
          _id
          title
          slug {
            current
          }
          _rawExcerpt
          duration
          series {
            _id
            title
            orderRank
            slug {
              current
            }
            _rawExcerpt
          }
        }
      }
    }
  }
`;

export const SERIES_ICONS = [faCompass, faShieldHalved, faCalendarCheck, faGraduationCap];
export const SERIES_ICON_BG = ["bg-green-emerald", "bg-green-emerald", "bg-green-emerald"];
const INITIAL_COUNT = 4;

function groupTrainings(trainings) {
  const groupMap = new Map();
  trainings.forEach((training) => {
    (training.series || []).forEach((category) => {
      if (!category?._id || !category.slug?.current) return;
      if (!groupMap.has(category._id)) {
        groupMap.set(category._id, { category, trainings: [] });
      }
      groupMap.get(category._id).trainings.push(training);
    });
  });

  return [...groupMap.values()].sort((a, b) => {
    if (a.category.orderRank < b.category.orderRank) return -1;
    if (a.category.orderRank > b.category.orderRank) return 1;
    return 0;
  });
}

const StatPill = ({ label }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-white border border-dark-10 shadow-sm px-4 py-2 text-sm font-semibold text-dark-90">
    <span className="h-1.5 w-1.5 rounded-full bg-green-emerald" />
    {label}
  </span>
);

export const TrainingPlaylist = () => {
  const data = useStaticQuery(query);
  const trainings = (data.trainings.edges || []).map((edge) => edge.node);
  const groups = groupTrainings(trainings);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const handleExpandGroup = (categoryId) => {
    setExpandedGroups((prev) => new Set(prev).add(categoryId));
  };

  const totalHoursLabel = formatTotalHours(
    sumDurationsToSeconds(trainings.map((t) => t.duration))
  );

  return (
    <div className="flex flex-col gap-20 mt-8">
      <div className="flex justify-center gap-3">
        <StatPill label={`${trainings.length} course${trainings.length === 1 ? "" : "s"}`} />
        <StatPill label={`${groups.length} learning path${groups.length === 1 ? "" : "s"}`} />
        {totalHoursLabel && <StatPill label={`${totalHoursLabel}, start to finish`} />}
      </div>

      {groups.map(({ category, trainings: seriesTrainings }, index) => {
        const totalDurationLabel = formatTotalDuration(
          sumDurationsToSeconds(seriesTrainings.map((t) => t.duration))
        );
        const seriesIcon = SERIES_ICONS[index % SERIES_ICONS.length];
        const seriesIconBg = SERIES_ICON_BG[index % SERIES_ICON_BG.length];
        const isExpanded = expandedGroups.has(category._id);
        const visibleTrainings = isExpanded
          ? seriesTrainings
          : seriesTrainings.slice(0, INITIAL_COUNT);

        return (
          <div
            key={category._id}
            id={`group-${category.slug.current}`}
            className="flex flex-col gap-6 scroll-mt-24"
          >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <span
                  className={cn(
                    "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white",
                    seriesIconBg
                  )}
                >
                  <FontAwesomeIcon icon={seriesIcon} className="h-8 w-8 opacity-90" />
                </span>
                <div>
                  <Link to={`/academy/${category.slug.current}/`} className="group">
                    <h3 className="text-2xl font-semibold text-dark-90 tracking-tight group-hover:text-link transition-colors duration-150">
                      {category.title} <ArrowRight size="16" className="ml-1" />
                    </h3>
                  </Link>
                  <p className="text-sm font-semibold text-dark-60 mt-1">
                    {seriesTrainings.length} course{seriesTrainings.length === 1 ? "" : "s"}
                    {totalDurationLabel && (
                      <>
                        <span className="mx-1.5 opacity-40">&middot;</span>
                        {totalDurationLabel}
                      </>
                    )}
                  </p>
                </div>
              </div>
              {category._rawExcerpt && (
                <p className="text-secondary leading-snug sm:text-right sm:max-w-sm">
                  {toPlainText(category._rawExcerpt)}
                </p>
              )}
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visibleTrainings.map((training, trainingIndex) => {
                const durationLabel = formatDurationMinutes(training.duration);
                const isNewlyRevealed = trainingIndex >= INITIAL_COUNT;
                return (
                  <li
                    key={training._id}
                    className={isNewlyRevealed ? "animate-fade-in-up" : undefined}
                    style={isNewlyRevealed ? { animationDelay: `${(trainingIndex - INITIAL_COUNT) * 40}ms` } : undefined}
                  >
                    <Link
                      to={`/academy/${category.slug.current}/#${training.slug.current}`}
                      className="group flex h-full flex-col rounded-xl bg-white p-5 transition-shadow duration-150 hover:shadow-lg"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <span className="font-semibold text-xl text-dark-90 leading-snug">
                          {training.title}
                        </span>
                        {durationLabel && (
                          <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-green-ultralight text-green-deep text-sm font-semibold px-2.5 py-1 whitespace-nowrap opacity-80">
                            <FontAwesomeIcon icon={faClock} className="h-2.5 w-2.5" />
                            {durationLabel}
                          </span>
                        )}
                      </div>

                      {training._rawExcerpt && (
                        <p className="flex-1 text-secondary leading-relaxed mb-4">
                          {truncateText({
                            str: toPlainText(training._rawExcerpt),
                            titleStr: training.title,
                            max: 160,
                          })}
                        </p>
                      )}

                      <span className="mt-auto flex items-center gap-1.5 text-sm font-semibold text-dark-90 pt-3 border-t border-dark-10 group-hover:text-green-deep transition-colors duration-150">
                        Get started
                        <span className="inline-flex transition-transform duration-150 group-hover:translate-x-1">
                          <ArrowRight size={10} />
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {!isExpanded && seriesTrainings.length > INITIAL_COUNT && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => handleExpandGroup(category._id)}
                  className="group inline-flex items-center gap-2 rounded-full border border-dark-90 px-6 py-2.5 font-semibold text-dark-90 transition-colors duration-150 hover:bg-dark-90 hover:text-white"
                >
                  View all {seriesTrainings.length} courses
                  <span className="inline-flex transition-transform duration-150 group-hover:translate-x-1">
                    <ArrowRight size={10} />
                  </span>
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
